import { useCallback, useRef } from 'react';

/**
 * Debounces localStorage.setItem calls to prevent excessive writes.
 * Useful for frequently-changing data like movie ratings, watchlists, etc.
 */
export const useDebouncedLocalStorage = (delay: number = 500) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setItem = useCallback((key: string, value: string) => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        console.warn(`Failed to write to localStorage key "${key}":`, error);
      }
    }, delay);
  }, [delay]);

  const setItemImmediate = useCallback((key: string, value: string) => {
    // Clear any pending debounced write
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn(`Failed to write to localStorage key "${key}":`, error);
    }
  }, []);

  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return { setItem, setItemImmediate, flush };
}; 